// src/pages/Proposals/CreateProposalDialog.js

import React, { useCallback, useState, useEffect } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
  Paper,
  Typography,
  Stack,
  Snackbar,
} from "@mui/material";
import axios from "axios";
import { useSnackbar } from "notistack";

import { useAuth } from "../../context/AuthContext";

import SectionBox from "components/SectionBox";
import InstitutionSelect from "./FileDialog/forms/common/InstitutionSelect";
import InfrastructureSelect from "./FileDialog/forms/common/InfrastructureSelect";
import BeamlineSelect from "./FileDialog/forms/common/BeamlineSelect";
import TechniqueSelect from "./FileDialog/forms/common/TechniqueSelect";
// import ResearcherSelect from "./FileDialog/forms/common/ResearcherSelect";
import PISelect from "./FileDialog/forms/common/PISelect";
import CreatePiForm from "./FileDialog/CreatePiForm";
import "./CreatePiForm.css";
import HumanPiResolution from "./FileDialog/HumanPiResolution";
import ParticipantRow from "./FileDialog/ParticipantRow";

export default function CreateProposalDialog({ open, onClose, onCreated }) {
  // ----------------------
  //        1. STATES
  // --------------------
  const token = localStorage.getItem("access_token");
  const API_URL = process.env.REACT_APP_API_URL;
  const api = axios.create({
    baseURL: API_URL,
  });
  const { enqueueSnackbar } = useSnackbar();

  // INFO DEL USER
  const { user, setUser } = useAuth();
  const getCodeHint = () => {
    if (user?.username?.startsWith("cmam")) {
      return "Code must start with beamlines prefixes (e.g. TOF1234)";
    }
    return "Code must start with CNA-beamline (e.g. CNA-STD001/26) or P width codigointerno (e.g. P01760)";
  };

  const [highlightField, setHighlightField] = useState(null); // No olvides technique
  const [mode, setMode] = useState("external"); // "external" | "manual"
  const isExternal = mode === "external";
  const [isAutofilled, setIsAutoFilled] = useState(false);
  const [externalSnapshot, setExternalSnapshot] = useState(null); // No restaura el PI
  const [beamlineSnapshot, setBeamlineSnapshot] = useState(null);

  const [code, setCode] = useState("");
  const [title, setTitle] = useState("");
  const [abstract, setAbstract] = useState("");
  const [participantsText, setParticipantsText] = useState("");
  const [projectsText, setProjectsText] = useState("");
  const [submidate, setSubmidate] = useState(
    new Date().toISOString().split("T")[0],
  );

  const [values, setValues] = useState({
    institution: null,
    infrastructure: null,
    beamlines: [],
    techniques: [],
    pi: null,
  });

  const set = useCallback((k, v) => {
    setValues((prev) => {
      const next = { ...prev, [k]: v };

      if (k === "institution" && prev.institution !== v) {
        next.infrastructure = null;
        next.beamlines = [];
        next.techniques = [];
      }

      if (k === "infrastructure" && prev.infrastructure !== v) {
        next.beamlines = [];
        next.techniques = [];
      }

      return next;
    });
  }, []);

  // -----------------------------------------
  // -------  2. EXTERNAL DATABASE -----------
  // ------------ HANDLE LOOKUP ---------------
  const [beamlineWarning, setBeamlineWarning] = useState(null);
  const mapExternalProposal = (data) => {
    return {
      title: data.title || "",
      abstract: data.abstract || "",
      projects_text: "",
      participants_text: "",
      institutionAlias: data.node || "",
      beamlineAlias: data.beamline || "",
      submidate: data.subm_date || "",
    };
  };

  const institution_map = {
    CNA: {
      institution: { id: "1", name: "Centro Nacional de Aceleradores (CNA)" },
      infrastructure: { id: "1", name: "CNA Tandem 3 MV" },
      beamline: {
        MIC: { id: "2", name: "CNA-MIC" },
        STD: { id: "3", name: "CNA-STD" },
        IRR: { id: "4", name: "CNA-IRR" },
        EXT: { id: "6", name: "CNA-EXT" },

        DEFAULT: { id: "10", name: "CNA-Generic" },

        // fallback por infraestructura (MUY IMPORTANTE)
        BY_INFRA: {
          1: { id: "3", name: "CNA-STD" },
        },
      },
    },
    CMAM: {
      institution: {
        id: "8",
        name: "Centro de Micro-Análisis de Materiales (CMAM)",
      },
      infrastructure: { id: "8", name: "CMAM Tandem 5 MV" },
      beamline: { TOF: { id: "8", name: "CMAM-ERDA-TOF" } },
    },
  };

  // RESEARCHER MACTHING
  // Researchers, tanto PI como participanst son tomados de la base de datos externa y sus datos son comparados
  // con los de la base de datos interna del IABA-ICTS Metadata Portal y pueden pasar tres casos:
  //  1. Encontrar coincidencias: encuentra el researcher y lo añade
  //  2. Encuentra alguien parecido pero no está seguro, así que le da opción al usuario de elegir si es ese, si no de crearlos
  //  3. No lo encuentra y hay que crearlom, por que el pi es obligatorio

  // Internamente el sistema hace esto, y envía aqui en la variable pi_resolution alguna de estas 3 opciones
  // "auto" asigna automáticamente
  // "human" muestra opciones elegibles y permite que el usuario lo asigne
  // "create" pregunta si quiere crear usuario y crea con datos proporcionados
  const [openCreatePi, setOpenCreatePi] = useState(false);
  const [notification, setNotification] = useState(null);
  const showNotification = (msg, type = "info") => {
    setNotification({ msg, type });
  };
  // Construir un step visual
  const [step, setStep] = useState("resolve"); // resolve | select | create | done
  const [pi, setPi] = useState(null);
  const [piResolution, setPiResolution] = useState(null);
  const [piCandidates, setPiCandidates] = useState([]);
  const [proposedPi, setProposedPi] = useState({
    name: "",
    familyname: "",
    email: "",
    orcid: "",
    suffix: "",
  });
  const buildPayload = (pi) => ({
    ...pi,
    familyname: pi.suffix ? `${pi.familyname} - ${pi.suffix}` : pi.familyname,
  });
  useEffect(() => {
    if (step === "create") {
      requestAnimationFrame(() => {
        setOpenCreatePi(true);
      });
    } else {
      setOpenCreatePi(false);
    }
  }, [step]);
  const handlePi = async () => {
    try {
      // Recargamos la info del PI (caso "auto") o añadimos diferenciador, cuando el nombre es el mismo (caso "human")
      const payloadPi = buildPayload(proposedPi);
      console.log("payload", payload);
      const res = await api.post("/researcher/create-pi/", payloadPi, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      set("pi", res.data.id);
      setStep("done");
      setNotification("PI created successfully", "success");
    } catch (err) {
      if (err.response?.status === 409) {
        showNotification(
          "A PI with the same name already exists. Please modify surname or select existing PI.",
          "warning",
        );
      } else {
        showNotification("Error creating PI", "error");
      }
    }
  };

  // MATCH PARTICIPANTS
  // auto → OK visual
  // human → dropdown ligero
  // external → texto informativo / opcional create
  const [participantsResolution, setParticipantsResolution] = useState([]);
  const [participants, setParticipants] = useState([]);
  const updateParticipant = (index, updates) => {
    setParticipants((prev) => {
      const copy = [...prev];
      copy[index] = {
        ...copy[index],
        ...updates,
      };
      return copy;
    });
  };
  const participantsPayload = participants.map((p) => {
    // AUTO
    if (p.type === "auto") {
      return {
        researcher_id: p.researcher.id,
      };
    }

    // HUMAN -> existing selected
    if (p.type === "human" && p.selectedId) {
      return {
        researcher_id: p.selectedId,
      };
    }

    // CREATE or HUMAN differentiated/new
    return {
      name: p.participant_data.name,
      familyname: p.participant_data.familyname,
      institution: p.participant_data.institution,
    };
  });

  // PROJECT
  const [project_data, setProjectData] = useState("");

  // HACER LA BUSQUEDA

  const handleLookup = async () => {
    try {
      console.log("code", code);
      if (!code) return;

      const url = `${API_URL}/proposal/fetch-proposal/?code=${code}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      if (!response.ok) {
        enqueueSnackbar(data.error || "Not allowed", {
          variant: "error",
        });
        return;
      }

      console.log("Funciona?", data);
      // console.log("PAYLOAD", payload);

      // Me falta hacer las validacione

      // MAPEO
      const mapped = mapExternalProposal(data);
      const mapping = institution_map[mapped.institutionAlias];

      if (!mapping) {
        console.warn("Institution not found in map:", mapped.institutionAlias);
        return;
      }

      // SET CAMPOS
      setTitle(mapped.title);
      setAbstract(mapped.abstract);
      setProjectsText(mapped.projects_text);
      setParticipantsText(mapped.participants_text);
      setSubmidate(mapped.submidate);
      set("institution", Number(mapping.institution.id));
      set("infrastructure", Number(mapping.infrastructure.id));

      const beamlineKey = mapped.beamlineAlias;
      const infraId = mapping.infrastructure.id;

      // MATCH BEAMLINE
      const matches = [];
      const directMatch = Boolean(mapping.beamline?.[beamlineKey]);
      const infraMatch = Boolean(mapping.beamline?.BY_INFRA?.[infraId]);

      if (mapping.beamline?.[beamlineKey]) {
        matches.push(mapping.beamline[beamlineKey]);
      }
      if (mapping.beamline?.BY_INFRA?.[infraId]) {
        matches.push(mapping.beamline.BY_INFRA[infraId]);
      }
      if (matches.length === 0 && mapping.beamline?.DEFAULT) {
        matches.push(mapping.beamline.DEFAULT);
      }
      const unique = Array.from(
        new Map(matches.map((b) => [b.id, b])).values(),
      );
      setBeamlineWarning(
        beamlineKey && !directMatch && !infraMatch
          ? `Beamline "${beamlineKey}" in ${mapped.institutionAlias} is not available for this facility at the moment.`
          : null,
      );

      if (unique.length > 0) {
        set(
          "beamlines",
          unique.map((b) => Number(b.id)),
        );
      }

      // MATCH PI (resoluciones)

      switch (data.pi_resolution) {
        // Asignación automática
        case "auto":
          setPiResolution("auto");
          set("pi", data.researcher_id);
          setStep("done");
          setNotification("PI found automatically");
          break;

        // Asignación human
        case "human":
          setPiResolution("human");
          setPiCandidates(data.candidates);
          setProposedPi(data.pi);
          setStep("select");
          break;

        //  Crear un nuevo PI
        case "create":
          setPiResolution("create");
          setProposedPi(data.proposed_pi);
          setStep("create");
          showNotification(
            "PI not found. A new PI is required. Please confirm the creation",
            "warning",
          );
          break;
      }

      // MATCH PARTICIPANTS (resoluciones)
      setParticipantsResolution(data.participants_resolution);
      console.log(data.participants_resolution);
      setParticipants(
        data.participants_resolution.map((p, index) => ({
          ...p,
          index,
          selectedId: null,
          status: p.type, // auto | human | external
        })),
      );
      setProjectData(data.project_data);
      setProjectsText(data.project_data?.title || "");
      setIsAutoFilled(true);

      // AVISO: Escoger una technique
      setHighlightField("techniques");
      enqueueSnackbar(
        "Proposal loaded successfully. Don't forget to select the technique to be applied.",
        { variant: "info" },
      );

      // SNAPSHOT: guardar última info recuperada del lookup para que cuando cambia a manual, external la conserve
      setExternalSnapshot({
        title: mapped.title,
        abstract: mapped.abstract,
        project: mapped.projects_text,
        submidate: mapped.submidate,
        // participants: mapped.participants_text,
        institution: Number(mapping.institution.id) || null,
        infrastructure: Number(mapping.infrastructure.id) || null,
        pi: pi || null,
      });
      setBeamlineSnapshot({
        ids: unique.map((b) => b.id),
        source: {
          directMatch,
          infraMatch,
          beamlineKey,
          usedDefault: matches.length === 0,
        },
      });

      setMode("external");
    } catch (error) {
      console.log(error);
      enqueueSnackbar(`Network error while fetching proposal ${error}`, {
        variant: "error",
      });
    }
  };

  const normalize = (v) => ({
    ...v,
    institution: v.institution?.id ?? v.institution,
    infrastructure: v.infrastructure?.id ?? v.infrastructure,
    pi: v.pi?.id ?? v.pi,
    beamlines: v.beamlines?.map((b) => b.id ?? b) ?? [],
    techniques: v.techniques?.map((t) => t.id ?? t) ?? [],
  });

  const payload = {
    source: mode,
    code: mode === "external" ? code : null,
    ...normalize(values),
    title,
    abstract,
    submission_date: new Date().toISOString().split("T")[0],
    participants_text: !isExternal ? participantsText : "",
    participants: isExternal ? participantsPayload : [],

    projects_text: !isExternal ? projectsText : null,
    project_data: isExternal ? project_data : undefined,
  };
  const restoreExternal = () => {
    console.log("restore estamos");
    if (mode === "external" && externalSnapshot) {
      setTitle(externalSnapshot.title);
      setAbstract(externalSnapshot.abstract);
      setProjectsText(externalSnapshot.project);
      setSubmidate(externalSnapshot.submidate);
      if (beamlineSnapshot) {
        console.log(beamlineSnapshot.ids);
        set("beamlines", beamlineSnapshot.ids.map(Number));
      }
      // set("pi", externalSnapshot.pi || null);
      set("institution", externalSnapshot.institution || null);
      set("infrastructure", externalSnapshot.infrastructure || null);
    }
  };

  // --------------------------------------------
  //  3. Create proposal with search proposal
  // ----------------------------------------
  const [errors, setErrors] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    messages: [],
  });

  const validateForm = () => {
    const newErrors = [];

    if (!values.pi) newErrors.push("PI");
    if (!values.beamlines?.length) newErrors.push("Beamlines");
    if (!values.techniques?.length) newErrors.push("Techniques");
    if (!title) newErrors.push("Title");

    setErrors(newErrors);

    if (newErrors.length > 0) {
      enqueueSnackbar(
        `Please complete required fields: ${newErrors.join(", ")}`,
        { variant: "error" },
      );

      if (newErrors.includes("PI")) {
        setStep("create"); // o "select"
      }
      return false;
    }

    return true;
  };

  const handleCreate = async () => {
    // POST al backend
    if (!validateForm()) return;
    console.log(payload);
    const response = await fetch(`${API_URL}/proposals/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`, // tu token
      },
      body: JSON.stringify(payload),
    });
    console.log("TOKEN:", token);

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);

      console.error("Backend error:", errorData);

      Object.entries(errorData).forEach(([field, messages]) => {
        enqueueSnackbar(`${field}: ${messages.join(", ")}`, {
          variant: "error",
        });
      });

      return;
    }
    onCreated();
    onClose();
  };

  // ------ RESET FORM / vacía el formulario con un click
  const resetForm = () => {
    setTitle("");
    setAbstract("");
    setParticipantsText("");
    setProjectsText("");
    setCode("");
    set("infrastructure", null);
    set("institution", null);
    set("pi", null);
    set("beamlines", []);
    set("techniques", []);
    setSubmidate("");
  };
  useEffect(() => {
    if (mode === "external" && externalSnapshot) {
      restoreExternal();
    }
  }, [mode]);

  // ------------------------------------------------------
  // ------------- CREATE PROPOSAL FORM -------------------
  // ------------------------------------------------------
  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        TransitionComponent={undefined}
      >
        <DialogTitle>Create Proposal</DialogTitle>

        <DialogContent dividers>
          {/* ---------------- MODE SELECTOR ---------------- */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              backgroundColor: "#fafafa",
            }}
          >
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              Proposal source
            </Typography>
            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={(e, newMode) => {
                if (!newMode) return;
                setMode(newMode);
              }}
              sx={{
                gap: 2,

                // 🔥 IMPORTANTE: quitar comportamiento "grouped"
                "& .MuiToggleButtonGroup-grouped": {
                  border: "1px solid #d0d7de",
                  margin: 0,
                },

                "& .MuiToggleButton-root": {
                  borderRadius: "12px !important",
                  px: 2,
                  py: 1,
                },

                "& .MuiToggleButton-root.Mui-selected": {
                  backgroundColor: "#e3f2fd",
                  borderColor: "#1976d2",
                  color: "#1976d2",
                },

                "& .MuiToggleButton-root.Mui-selected:hover": {
                  backgroundColor: "#d6ecff",
                },
              }}
            >
              <ToggleButton value="external">
                <Typography variant="subtitle3" sx={{ mb: 1, fontWeight: 600 }}>
                  Import from Beamtime Portal
                </Typography>
              </ToggleButton>
              <ToggleButton value="manual">
                <Typography variant="subtitle3" sx={{ mb: 1, fontWeight: 600 }}>
                  Manual entry
                </Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          </Paper>

          {/* ---------------- EXTERNAL LOOKUP ---------------- */}

          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              border: "1px solid #e0e0e0",
              borderRadius: 2,
            }}
          >
            {isExternal && (
              <>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  External proposal lookup
                </Typography>

                <Stack direction="row" spacing={2}>
                  <TextField
                    label="Code"
                    fullWidth
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  <Button onClick={handleLookup} variant="contained">
                    Search proposal
                  </Button>
                </Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: -1, mb: 3, display: "block" }}
                >
                  {getCodeHint()}
                </Typography>
              </>
            )}
            <Typography variant="h6" sx={{ mb: 3 }}>
              General information
            </Typography>

            <Grid container spacing={3} alignItems="center">
              {/* xs -> Moviles, sm -> tablets, md -> escritorio Todo es sobre /12   */}
              <Grid item xs={12} sm={4} md={6}>
                <InstitutionSelect
                  value={values.institution}
                  onChange={(id) => set("institution", id)}
                  required
                />
              </Grid>
              <Grid item xs={6} sm={4} md={6}>
                <InfrastructureSelect
                  key={values.institution || "no-inst"}
                  value={values.infrastructure}
                  onChange={(id) => set("infrastructure", id)}
                  institution={values.institution}
                  refetchOnEndpointChange={true}
                  required
                />
              </Grid>
            </Grid>

            {/* ---------------- PI LOOKUP ---------------- */}

            {isExternal ? (
              <>
                <PISelect
                  value={values.pi}
                  onChange={(id) => set("pi", id)}
                  candidates={piCandidates || []}
                  disabled={piResolution === "auto"}
                  onCreateRequest={() => setStep("create")}
                  required
                />
                {step === "create" && ( // STEP CREATE
                  <Dialog
                    open={step === "create"}
                    TransitionComponent={undefined}
                    onClose={() => setStep("select")}
                    maxWidth="sm"
                    fullWidth
                    disableScrollLock
                    BackdropProps={{
                      sx: { backgroundColor: "rgba(0,0,0,0.3)" }, // menos oscuro
                    }}
                  >
                    <DialogTitle>Create new Principal Investigator</DialogTitle>

                    <DialogContent dividers>
                      {openCreatePi && (
                        <CreatePiForm
                          proposedPi={proposedPi}
                          setProposedPi={setProposedPi}
                          onCancel={() => setStep("select")}
                          onCreate={handlePi}
                        />
                      )}

                      {notification && (
                        <Alert severity={notification.type} sx={{ mt: 2 }}>
                          {notification.msg}
                        </Alert>
                      )}
                    </DialogContent>
                  </Dialog>
                )}
                {step === "select" && ( // Step human select
                  <Dialog
                    open={step === "select" && piCandidates?.length > 0}
                    TransitionComponent={undefined}
                    onClose={() => setStep("select")}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                      sx: {
                        borderRadius: 3,
                      },
                    }}
                  >
                    <DialogTitle
                      sx={{
                        pb: 1,
                        fontWeight: 600,
                      }}
                    >
                      Similar researchers found
                    </DialogTitle>

                    <DialogContent dividers>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 3 }}
                      >
                        We found researchers with similar names in the database.
                        Please select an existing PI or differentiate the new
                        one.
                      </Typography>
                      <HumanPiResolution
                        candidates={piCandidates}
                        proposedPi={proposedPi}
                        setProposedPi={setProposedPi}
                        selectedPi={values.pi}
                        onSelect={(id) => {
                          set("pi", id);
                          setStep("done");
                        }}
                        onCreateRequest={handlePi}
                      />

                      {notification && (
                        <Alert severity={notification.type} sx={{ mt: 2 }}>
                          {notification.msg}
                        </Alert>
                      )}
                    </DialogContent>
                  </Dialog>
                )}

                {/* {step === "done" && (
                  <div className="step-box success">
                    <h4>Done</h4>
                    <div style={{ padding: 12 }}>✅ PI selected: {pi}</div>
                  </div>
                )} */}
              </>
            ) : (
              <PISelect value={values.pi} onChange={(id) => set("pi", id)} />
            )}

            {/* ------ PROPOSAL INFO -------- */}
            <Grid item xs={12} sm={4} md={6} pt={1}>
              <BeamlineSelect
                key={values.infrastructure || "no-infra"}
                value={values.beamlines}
                onChange={(ids) => set("beamlines", ids)}
                infrastructure={values.infrastructure}
                refetchOnEndpointChange={true}
                InputProps={{
                  readOnly: isExternal,
                }}
                required
                error={errors.beamlines}
                helperText={
                  errors.beamlines ? "At least one beamline is required" : ""
                }
              />
              {beamlineWarning && isExternal && (
                <Alert
                  severity="warning"
                  sx={{
                    mt: 1,
                    py: 0,
                    px: 1,
                    fontSize: "0.85rem",
                    alignItems: "center",

                    "& .MuiAlert-icon": {
                      fontSize: "1rem",
                      padding: 0,
                      mr: 1,
                    },
                  }}
                >
                  {beamlineWarning}
                </Alert>
              )}
            </Grid>
            <Grid item xs={12} sm={4} md={6} pt={1}>
              <TechniqueSelect
                label="Techniques linked"
                value={values.techniques}
                onChange={(ids) => set("techniques", ids)}
                required
                error={errors.beamlines}
                helperText={
                  errors.beamlines ? "Select at least one technique" : ""
                }
                highlighted={highlightField === "techniques"}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={6}>
              <TextField
                label="Title"
                fullWidth
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                InputProps={{
                  readOnly: isExternal,
                }}
                required
              />
            </Grid>

            <Grid item xs={12} sm={4} md={6} pt={1}>
              <TextField
                label="Abstract"
                fullWidth
                multiline
                minRows={4}
                value={abstract}
                onChange={(e) => setAbstract(e.target.value)}
                InputProps={{
                  readOnly: isExternal,
                }}
              />
            </Grid>

            <Grid item xs={12} sm={4} md={6} pt={1}>
              <TextField
                fullWidth
                label="Projects"
                value={projectsText}
                onChange={(e) => setProjectsText(e.target.value)}
                InputProps={{
                  readOnly: isExternal,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={6} pt={1}>
              <TextField
                fullWidth
                label="Submission date"
                type="date"
                value={submidate}
                onChange={(e) => setSubmidate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: new Date().toISOString().split("T")[0],
                  readOnly: isExternal,
                }}
              />
              {isExternal && participants.length > 0 && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      border: "1px solid rgba(0,0,0,0.23)",
                      borderRadius: 1,
                      px: 2,
                      py: 1.5,
                      mt: 2,
                      backgroundColor: "#fff",
                    }}
                  >
                    {/* Label estilo TextField */}
                    <Typography
                      variant="caption"
                      sx={{
                        position: "absolute",
                        top: -10,
                        left: 12,
                        px: 0.5,
                        fontSize: "0.75rem",
                        color: "rgba(0, 0, 0, 0.6)",
                        backgroundColor: "#fff",
                      }}
                    >
                      Participants
                    </Typography>

                    {/* Content */}
                    <Box sx={{ mt: -1 }}>
                      {participants.map((p, i) => (
                        <ParticipantRow
                          key={i}
                          participant={p}
                          onSelect={(researcherId) => {
                            updateParticipant(i, {
                              status: "auto",
                              selectedId: researcherId,
                            });
                          }}
                          onCreate={(data) => {
                            updateParticipant(i, {
                              status: "external",
                              data,
                            });
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                </Grid>
              )}

              {!isExternal && (
                <>
                  <Grid item xs={12} sm={4} md={6} pt={1}>
                    <TextField
                      fullWidth
                      label="Participants"
                      value={participantsText}
                      onChange={(e) => setParticipantsText(e.target.value)}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Paper>

          {/* ---------------- ALERTS ---------------- */}

          {isExternal && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Data will be imported from external system and marked as approved.
            </Alert>
          )}
          {mode === "manual" && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This proposal will be marked as "Pending review".
            </Alert>
          )}
        </DialogContent>

        {mode === "manual" && (
          <Button color="warning" onClick={resetForm}>
            Clear form
          </Button>
        )}
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="error" sx={{ width: "100%" }}>
          {snackbar.messages.map((msg, i) => (
            <div key={i}>{msg}</div>
          ))}
        </Alert>
      </Snackbar>
    </>
  );
}
